import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import FetchingSpinner from "../../common/FetchingSpinner";
import DashboardPeriodPicker from "../dashboard/statistics/DashboardPeriodPicker";
import { getCurrentMonthPeriod, validatePeriod } from "../dashboard/statistics/dashboardStatisticsUtils";
import {
  fetchPayrollDashboard,
  formatPayrollMoney,
  formatPercent,
  trendBarHeights,
} from "./payrollStatisticsApi";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";

const COST_SLICE_COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#3b82f6",
  "#ef4444",
  "#64748b",
];

const RUN_STATUS_TONE = {
  Processed: "success",
  Paid: "success",
  Draft: "muted",
  Processing: "primary",
};

const PayrollOverviewPage = () => {
  const { t } = useTranslation();
  const defaultPeriod = getCurrentMonthPeriod();
  const [activePresetId, setActivePresetId] = useState("thisMonth");
  const [draftFrom, setDraftFrom] = useState(defaultPeriod.periodFrom);
  const [draftTo, setDraftTo] = useState(defaultPeriod.periodTo);
  const [appliedFrom, setAppliedFrom] = useState(defaultPeriod.periodFrom);
  const [appliedTo, setAppliedTo] = useState(defaultPeriod.periodTo);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { dashboard: data, message } = await fetchPayrollDashboard(appliedFrom, appliedTo);
      if (!data) {
        setDashboard(null);
        setError(message || t("app.payroll.overview.loadFailed"));
        return;
      }
      setDashboard(data);
    } catch (err) {
      setDashboard(null);
      setError(err.response?.data?.message ?? err?.message ?? t("app.payroll.overview.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const applyPeriod = (from, to, presetId) => {
    if (!validatePeriod(from, to).valid) return;
    setAppliedFrom(from);
    setAppliedTo(to);
    setActivePresetId(presetId ?? null);
  };

  const handleApplyPeriod = () => {
    if (validatePeriod(draftFrom, draftTo).valid) {
      applyPeriod(draftFrom, draftTo, activePresetId);
    }
  };

  const handlePresetSelect = (presetId, from, to) => {
    setDraftFrom(from);
    setDraftTo(to);
    applyPeriod(from, to, presetId);
  };

  const cost = dashboard?.costSummary;
  const structures = dashboard?.structures;
  const trends = dashboard?.trends ?? [];
  const graphs = dashboard?.graphs;
  const barHeights = trendBarHeights(trends, "grossPay");
  const costBreakdown = graphs?.costBreakdown?.length
    ? graphs.costBreakdown
    : cost?.costBreakdown ?? [];
  const usageByStructure = structures?.usageByStructure ?? [];
  const runsByStatus = graphs?.runsByStatus ?? [];

  const statValue = (value) => (loading && !dashboard ? "—" : value);

  return (
    <div className="documents-page payroll-page payroll-overview-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-cash-stack" />}
        title={t("app.payroll.overview.title")}
        description={t("app.payroll.overview.subtitle")}
      />

      <DashboardPeriodPicker
        periodFrom={draftFrom}
        periodTo={draftTo}
        onPeriodFromChange={(d) => {
          setActivePresetId(null);
          setDraftFrom(d);
        }}
        onPeriodToChange={(d) => {
          setActivePresetId(null);
          setDraftTo(d);
        }}
        onApply={handleApplyPeriod}
        resolvedPeriod={dashboard}
        activePresetId={activePresetId}
        onPresetSelect={handlePresetSelect}
      />

      {error && (
        <div className="alert alert-danger mb-3">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
          <button type="button" className="btn btn-link btn-sm ms-2 p-0 align-baseline" onClick={loadDashboard}>
            {t("app.payroll.overview.retry")}
          </button>
        </div>
      )}

      {loading && !dashboard ? (
        <FetchingSpinner text={t("app.payroll.overview.loading")} />
      ) : (
        <div className="payroll-overview-body">
          {loading && dashboard && (
            <div className="payroll-overview-refresh-hint">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t("app.payroll.overview.loading")}
            </div>
          )}

          <section className="ts-appr-stats payroll-overview-stats" aria-label={t("app.payroll.overview.statsAria")}>
            <div className="ts-appr-stat-card ts-appr-stat-card--total">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon">
                    <i className="bi bi-collection-play" />
                  </span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">{statValue(cost?.payrollRunCount ?? 0)}</span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.overview.statRuns")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-appr-stat-card ts-appr-stat-card--approved">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon">
                    <i className="bi bi-people-fill" />
                  </span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">{statValue(cost?.uniqueEmployeeCount ?? 0)}</span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.overview.statEmployees")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-appr-stat-card ts-appr-stat-card--awaiting">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon">
                    <i className="bi bi-pie-chart-fill" />
                  </span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">
                      {statValue(formatPercent(cost?.payrollToCommitmentRatioPercent ?? 0))}
                    </span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.overview.statRatio")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-appr-stat-card ts-appr-stat-card--returned">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon">
                    <i className="bi bi-wallet2" />
                  </span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">
                      {statValue(formatPercent(cost?.netToGrossRatioPercent ?? 0))}
                    </span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.overview.statNetRatio")}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="payroll-overview-layout">
            <div className="payroll-overview-layout__main">
              <div className="payroll-overview-panel">
                <header className="payroll-overview-panel__head">
                  <h3 className="payroll-overview-panel__title">
                    <i className="bi bi-graph-up-arrow" aria-hidden />
                    {t("app.payroll.overview.trendsTitle")}
                  </h3>
                </header>
                {trends.length === 0 ? (
                  <p className="payroll-overview-empty">{t("app.payroll.overview.trendsEmpty")}</p>
                ) : (
                  <div className="payroll-overview-trend" role="img" aria-label={t("app.payroll.overview.trendsTitle")}>
                    <div className="payroll-overview-trend__chart">
                      {barHeights.map((h, i) => (
                        <div key={trends[i]?.label || i} className="payroll-overview-trend__col">
                          <div className="payroll-overview-trend__col-inner">
                            <span className="payroll-overview-trend__value">
                              {formatPayrollMoney(trends[i]?.grossPay)}
                            </span>
                            <div
                              className="payroll-overview-trend__bar"
                              style={{ height: `${Math.max(h, 6)}%` }}
                            />
                          </div>
                          <span className="payroll-overview-trend__label">{trends[i]?.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="payroll-overview-panel">
                <header className="payroll-overview-panel__head">
                  <h3 className="payroll-overview-panel__title">
                    <i className="bi bi-diagram-3" aria-hidden />
                    {t("app.payroll.overview.structuresTitle")}
                  </h3>
                  <span className="payroll-overview-chip">
                    {t("app.payroll.overview.structuresActive", {
                      count: structures?.activeAssignmentCount ?? 0,
                    })}
                  </span>
                </header>
                {usageByStructure.length === 0 ? (
                  <p className="payroll-overview-empty">{t("app.payroll.overview.structuresEmpty")}</p>
                ) : (
                  <ul className="payroll-overview-structure-list">
                    {usageByStructure.map((row) => (
                      <li key={row.structureId ?? row.structureName} className="payroll-overview-structure-row">
                        <span className="payroll-overview-structure-row__icon" aria-hidden>
                          <i className="bi bi-layers" />
                        </span>
                        <div className="payroll-overview-structure-row__body">
                          <span className="payroll-overview-structure-row__name">{row.structureName}</span>
                          <span className="payroll-overview-structure-row__meta">
                            {t("app.payroll.overview.structureMeta", {
                              employees: row.uniqueEmployeeCount,
                              gross: formatPayrollMoney(row.totalGrossPay),
                            })}
                          </span>
                        </div>
                        <span className="payroll-overview-structure-row__pct">
                          {formatPercent(row.percentOfTotalGross)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <aside className="payroll-overview-layout__side">
              <div className="payroll-overview-panel payroll-overview-panel--hero">
                <header className="payroll-overview-panel__head payroll-overview-panel__head--hero">
                  <h3 className="payroll-overview-panel__title payroll-overview-panel__title--hero">
                    {t("app.payroll.overview.costTitle")}
                  </h3>
                </header>
                <p className="payroll-overview-hero__amount">{formatPayrollMoney(cost?.totalGrossPay ?? 0)}</p>
                <p className="payroll-overview-hero__hint">{t("app.payroll.overview.costHint")}</p>
                <div className="payroll-overview-hero__metrics">
                  <div className="payroll-overview-hero__metric">
                    <span className="payroll-overview-hero__metric-label">{t("app.payroll.overview.netPay")}</span>
                    <span className="payroll-overview-hero__metric-value">{formatPayrollMoney(cost?.totalNetPay ?? 0)}</span>
                  </div>
                  <div className="payroll-overview-hero__metric">
                    <span className="payroll-overview-hero__metric-label">{t("app.payroll.overview.deductions")}</span>
                    <span className="payroll-overview-hero__metric-value">
                      {formatPayrollMoney(cost?.totalDeductions ?? 0)}
                    </span>
                  </div>
                  <div className="payroll-overview-hero__metric">
                    <span className="payroll-overview-hero__metric-label">{t("app.payroll.overview.committed")}</span>
                    <span className="payroll-overview-hero__metric-value">
                      {formatPayrollMoney(cost?.committedSalaryAmount ?? 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="payroll-overview-panel">
                <header className="payroll-overview-panel__head">
                  <h3 className="payroll-overview-panel__title">
                    <i className="bi bi-pie-chart" aria-hidden />
                    {t("app.payroll.overview.breakdownTitle")}
                  </h3>
                </header>
                {costBreakdown.length === 0 ? (
                  <p className="payroll-overview-empty">{t("app.payroll.overview.breakdownEmpty")}</p>
                ) : (
                  <ul className="payroll-overview-breakdown">
                    {costBreakdown.map((slice, idx) => {
                      const pct = slice.percent ?? slice.percentOfGross ?? 0;
                      const amount = slice.value ?? slice.amount ?? 0;
                      const color = COST_SLICE_COLORS[idx % COST_SLICE_COLORS.length];
                      return (
                        <li key={slice.key || slice.label} className="payroll-overview-breakdown__item">
                          <div className="payroll-overview-breakdown__row">
                            <span className="payroll-overview-breakdown__dot" style={{ backgroundColor: color }} />
                            <span className="payroll-overview-breakdown__label">{slice.label}</span>
                            <span className="payroll-overview-breakdown__amount">{formatPayrollMoney(amount)}</span>
                            <span className="payroll-overview-breakdown__pct">{formatPercent(pct, 1)}</span>
                          </div>
                          <div className="payroll-overview-breakdown__track">
                            <div
                              className="payroll-overview-breakdown__fill"
                              style={{
                                width: `${Math.min(Math.max(pct, 0), 100)}%`,
                                backgroundColor: color,
                              }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="payroll-overview-panel">
                <header className="payroll-overview-panel__head">
                  <h3 className="payroll-overview-panel__title">
                    <i className="bi bi-circle-half" aria-hidden />
                    {t("app.payroll.overview.runsTitle")}
                  </h3>
                </header>
                {runsByStatus.length === 0 ? (
                  <p className="payroll-overview-empty">{t("app.payroll.overview.runsEmpty")}</p>
                ) : (
                  <ul className="payroll-overview-runs">
                    {runsByStatus.map((run) => {
                      const tone = RUN_STATUS_TONE[run.key] || "muted";
                      return (
                        <li key={run.key || run.label} className={`payroll-overview-run payroll-overview-run--${tone}`}>
                          <span className="payroll-overview-run__count">{run.value}</span>
                          <div className="payroll-overview-run__body">
                            <div className="payroll-overview-run__top">
                              <span className="payroll-overview-run__label">{run.label}</span>
                              <span className="payroll-overview-run__pct">{formatPercent(run.percent, 1)}</span>
                            </div>
                            <div className="payroll-overview-run__track">
                              <div
                                className="payroll-overview-run__fill"
                                style={{ width: `${Math.min(Math.max(run.percent, 0), 100)}%` }}
                              />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </aside>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollOverviewPage;
